import { Link } from 'react-router';
import {
  SEO,
  BreadcrumbSchema,
  CollectionPageSchema,
  FAQSchema,
  ItemListSchema,
  SpeakableSchema,
} from '@/components/seo/seo';
import { SmartCTA } from '@/components/ui/smart-cta';
import { blogCategories, blogPosts, type BlogPostSummary } from '@/data/blog-posts';

const categoryById = new Map(blogCategories.map((category) => [category.id, category]));

const startHereQuestions = [
  {
    question: 'What should I read first after hitting a pothole?',
    answer:
      'After hitting a pothole, first document the road defect, vehicle damage, exact location, date, time, and any repair costs before evidence is lost. Fixtyer evidence guides explain which photos, invoices, garage notes, and council records help support a UK pothole damage claim.',
    links: [
      {
        to: '/blog/what-evidence-do-you-need-for-pothole-damage-claim',
        label: 'Evidence checklist',
      },
      {
        to: '/blog/new-year-same-potholes',
        label: 'Protect a fresh claim',
      },
    ],
  },
  {
    question: 'What should I read if pothole damage appears days or weeks later?',
    answer:
      'If pothole damage appears days or weeks later, look for delayed symptoms such as tyre sidewall bulges, steering pull, suspension knocks, wheel alignment issues, or uneven tyre wear. A prompt mechanic inspection can help link the damage to an impact rather than ordinary wear and tear.',
    links: [
      {
        to: '/blog/mechanics-view-hidden-pothole-damage',
        label: "Mechanic's view",
      },
      {
        to: '/blog/spring-pothole-season',
        label: 'Delayed damage guide',
      },
    ],
  },
  {
    question: 'What should I read if the council rejected my pothole claim?',
    answer:
      'If the council rejected your pothole claim, check whether the response relies on a Section 58 defence, inspection records, repair thresholds, lack of prior reports, or missing evidence. A rejection is not always final, but the next step depends on what reason the council gave.',
    links: [
      {
        to: '/blog/council-rejected-your-pothole-claim',
        label: 'Rejected claim guide',
      },
    ],
  },
  {
    question: 'Are Fixtyer blog articles legal advice?',
    answer:
      'No. Fixtyer blog articles provide general information and claim preparation guidance for UK drivers. Fixtyer is not a law firm, does not provide legal advice, does not act on behalf of drivers, and does not guarantee compensation.',
    links: [
      {
        to: '/how-it-works',
        label: 'How Fixtyer works',
      },
    ],
  },
];

function formatDate(date: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    // Date-only strings parse as UTC midnight; format in UTC so the
    // prerendered HTML matches the client in every timezone (hydration).
    timeZone: 'UTC',
  }).format(new Date(date));
}

const latestFirst = [...blogPosts].sort(
  (a, b) => new Date(b.datePublished).getTime() - new Date(a.datePublished).getTime()
);

function BlogPostCard({ post }: { post: BlogPostSummary }) {
  const category = categoryById.get(post.category);

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block overflow-hidden bg-white rounded-2xl border-2 border-surface-200 hover:border-brand-300 hover:shadow-lg transition-all duration-300"
    >
      {post.image && (
        <img
          src={post.image}
          alt={post.imageAlt ?? ''}
          width={post.imageWidth}
          height={post.imageHeight}
          loading="lazy"
          decoding="async"
          className="w-full aspect-[1200/630] object-cover"
        />
      )}
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-brand-100 text-brand-700">
            {category?.title ?? 'Blog'}
          </span>
          <span className="text-sm text-surface-600">{post.readTime} read</span>
          <span className="text-sm text-surface-600">{formatDate(post.dateModified)}</span>
        </div>
        <h2 className="font-display text-xl font-bold text-brand-950 mb-3 group-hover:text-brand-700 transition-colors">
          {post.title}
        </h2>
        <p className="text-surface-700 mb-5">{post.description}</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {post.keywords.slice(0, 3).map((keyword) => (
            <span key={keyword} className="px-3 py-1 rounded-full bg-surface-50 text-xs font-medium text-surface-700 border border-surface-200">
              {keyword}
            </span>
          ))}
        </div>
        <span className="inline-flex items-center gap-1 text-brand-600 font-medium text-sm group-hover:text-accent-600 transition-colors">
          Read article
          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

export function BlogPage() {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
  ];
  const latestPost = latestFirst[0];
  const olderPosts = latestPost
    ? latestFirst.filter((post) => post.slug !== latestPost.slug)
    : latestFirst;

  return (
    <div className="relative">
      <SEO
        title="Pothole Claim Advice Blog | Evidence, Rejections & Road Damage | Fixtyer"
        description="Practical UK pothole claim advice covering evidence, council rejection letters, hidden vehicle damage, Section 58 defences, and road maintenance context."
        canonical="/blog"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <CollectionPageSchema
        name="Fixtyer Pothole Claim Blog"
        description="Articles covering UK pothole damage claims, council road maintenance data, evidence, and claim preparation."
        url="/blog"
      />
      {latestFirst.length > 0 && (
        <ItemListSchema
          name="Fixtyer Blog Articles"
          description="Practical Fixtyer articles for UK drivers dealing with pothole damage, claim evidence, and council responses."
          url="/blog"
          items={latestFirst.map((post) => ({
            name: post.title,
            url: `/blog/${post.slug}`,
            description: post.description,
          }))}
        />
      )}
      <FAQSchema faqs={startHereQuestions} />
      <SpeakableSchema
        url="/blog"
        headline="Fixtyer Pothole Claim Blog"
        cssSelectors={['[data-speakable="blog-summary"]', '[data-speakable="blog-start-here"]']}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-20">
        <div
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="blog-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#blog-grid)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">
              Home
            </Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Blog</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
            Practical pothole claim advice
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 leading-tight mb-6">
            Pothole Claim Advice and{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
              Road Damage Guides
            </span>
          </h1>
          <p data-speakable="blog-summary" className="text-lg text-surface-800 max-w-2xl mx-auto">
            Practical articles for UK drivers dealing with pothole damage, council claim responses,
            road maintenance data, and the evidence needed to prepare a stronger claim.
          </p>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {latestPost && (
            <div className="mb-12">
              <p className="font-display text-sm font-semibold uppercase tracking-wide text-brand-600 mb-4">
                Latest Article
              </p>
              <BlogPostCard post={latestPost} />
            </div>
          )}

          {olderPosts.length > 0 && (
            <>
              <p className="font-display text-sm font-semibold uppercase tracking-wide text-brand-600 mb-4">
                More Articles
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {olderPosts.map((post) => (
                  <BlogPostCard key={post.slug} post={post} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section data-speakable="blog-start-here" className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
              Start with the Situation You Are In
            </h2>
            <p className="text-surface-700">
              Each answer gives the next practical step and points you to the article most relevant
              to your situation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {startHereQuestions.map((item) => (
              <article key={item.question} className="bg-white rounded-2xl border border-surface-200 p-6">
                <h3 className="font-display text-xl font-bold text-brand-950 mb-3">
                  {item.question}
                </h3>
                <p className="text-surface-700 leading-relaxed mb-5">{item.answer}</p>
                <div className="flex flex-wrap gap-3">
                  {item.links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:border-brand-300 hover:bg-brand-100 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-4">
            Ready to Check Your Claim?
          </h2>
          <p className="text-brand-200 text-lg mb-8 max-w-xl mx-auto">
            Use Fixtyer to organise your evidence, understand the responsible authority, and prepare
            a clearer pothole damage claim pack.
          </p>
          <SmartCTA
            guestText="Start Your Free Claim Check"
            className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors"
            showArrow
          />
        </div>
      </section>

      <div
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`,
        }}
      />
    </div>
  );
}
